import sortBy from 'lodash/sortBy';
import qs from 'query-string';
import { useEffect, useRef, useState } from 'react';
import { ChakraSelect } from '../select';
import { getValidatedLimitParam } from './utils';

export interface IPaginationLimit {
  /**
   * total: is the total number of the paginated items
   */
  total: number;
  /**
   * setSearchParams: is a function returns a stringified param like that "?limit=45&page=1"
   * you can use it to update your search params by passing a function and using this stringified param
   * to update your search param
   */
  setSearchParams: (params: string) => void;
  /**
   * onChange: optional function to do something when change occurs
   */
  onChange?: (currentLimit: number) => void;
}

interface LimitOption {
  label: string;
  value: number;
}

const DEFAULT_LIMITS = [15, 30, 45];
const MAX_LIMIT = 200;

export const PaginationLimit = ({ total, setSearchParams, onChange }: IPaginationLimit) => {
  const currentLimit = getValidatedLimitParam();
  const params = useRef<Record<string, unknown>>({});
  const [typedLimit, setTypedLimit] = useState<number>();
  const [options, setOptions] = useState<LimitOption[]>([]);

  useEffect(() => {
    setTimeout(() => {
      params.current = qs.parse(window.location.search);
      params.current.limit = currentLimit;
      setSearchParams(qs.stringify(params.current, { skipEmptyString: true }));
    }, 200);
  }, [currentLimit]);

  useEffect(() => {
    const limits = [...DEFAULT_LIMITS];

    if (!limits.find((limit) => limit === currentLimit)) limits.push(currentLimit);
    if (!limits.find((limit) => limit === typedLimit) && typedLimit) limits.push(typedLimit);

    const limitOptions = limits.map((limit) => ({ label: `Display ${limit} rows from ${total}`, value: limit }));

    setOptions(sortBy(limitOptions, 'value'));
  }, [typedLimit, total, currentLimit]);

  const handleChange = (option: LimitOption | null) => {
    params.current.limit = option?.value;
    params.current.page = 1;
    setSearchParams(qs.stringify(params.current, { skipEmptyString: true }));
    onChange?.(option?.value as number);
  };

  const handleInputChange = (value: number) => {
    if (isNaN(value)) return;
    if (value >= MAX_LIMIT) return setTypedLimit(MAX_LIMIT);

    setTypedLimit(value);
  };

  return (
    <ChakraSelect
      size='sm'
      options={[...options]}
      closeMenuOnSelect
      selectedOptionStyle='check'
      value={options.filter((option) => option.value === currentLimit)}
      onChange={(option) => handleChange(option as LimitOption | null)}
      isMulti={false}
      menuPlacement='auto'
      onInputChange={(value) => handleInputChange(Number(value))}
      inputValue={typedLimit ? typedLimit.toString() : ''}
    />
  );
};
